/**
 * Базовые компоненты.
 *
 * Немного и намеренно: чем меньше вариантов у кнопки, тем меньше
 * шансов, что интерфейс расползётся. Всё остальное — обычный Tailwind
 * по месту.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { STATUS, avatarColor, initials } from '../lib/format';
import type { VideoStatus } from '../lib/types';

export function Pill({ status }: { status: VideoStatus }) {
  const s = STATUS[status];
  return <span className={`pill ${s.className}`}>{s.label}</span>;
}

export function OverduePill() {
  return <span className="pill bg-dang/10 text-dang">просрочено</span>;
}

export function Avatar({
  id,
  name,
  url,
  size = 32,
}: {
  id: string;
  name: string;
  url?: string | null;
  size?: number;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full grid place-items-center font-bold text-white shrink-0"
      style={{ width: size, height: size, background: avatarColor(id), fontSize: size / 2.75 }}
      title={name}
    >
      {initials(name)}
    </div>
  );
}

export function Person({
  id,
  name,
  url,
  sub,
  size = 30,
}: {
  id: string;
  name: string;
  url?: string | null;
  sub?: string;
  size?: number;
}) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <Avatar id={id} name={name} url={url} size={size} />
      <div className="min-w-0">
        <div className="font-semibold truncate">{name}</div>
        {sub && <div className="text-[11.5px] text-muted2 truncate">{sub}</div>}
      </div>
    </div>
  );
}

export function Stat({
  value,
  label,
  hint,
  tone,
}: {
  value: ReactNode;
  label: string;
  hint?: string;
  tone?: 'ok' | 'warn' | 'dang';
}) {
  const color = tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warn' : tone === 'dang' ? 'text-dang' : '';
  return (
    <div className="card">
      <div className={`text-[29px] font-extrabold tracking-[-0.035em] leading-tight mono ${color}`}>
        {value}
      </div>
      <div className="text-xs text-muted mt-1.5">{label}</div>
      {hint && <div className="text-[11px] text-muted2 mt-1">{hint}</div>}
    </div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="text-center py-14 text-muted2">
      <div className="text-4xl opacity-30 mb-3">◎</div>
      <div className="text-[15px] text-muted mb-1.5 font-semibold">{title}</div>
      {hint && <div className="text-[12.5px] leading-relaxed">{hint}</div>}
    </div>
  );
}

export function Loading({ rows = 3 }: { rows?: number }) {
  // Скелетоны вместо спиннера: интерфейс сразу показывает форму будущего
  // содержимого, и переход не ощущается как «моргание»
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-[70px] rounded-xl2 bg-surface border border-line" />
      ))}
    </div>
  );
}

export function Progress({
  percent,
  className = '',
  smooth,
}: {
  percent: number;
  className?: string;
  /** Для загрузки: значение приходит часто, длинная анимация только мешает */
  smooth?: boolean;
}) {
  return (
    <div className={`h-1.5 bg-[#1E1E2A] rounded overflow-hidden ${className}`}>
      <div
        className={`h-full bg-gradient-to-r from-accent to-accent2 transition-[width] ${
          smooth ? 'duration-150 ease-linear' : 'duration-300'
        }`}
        style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
      />
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <label className="label">{label}</label>
      {children}
      {hint && <div className="text-[11.5px] text-muted2 mt-1.5 leading-snug">{hint}</div>}
    </div>
  );
}

export function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`w-10 h-[23px] rounded-full relative shrink-0 transition-colors ${
        on ? 'bg-accent' : 'bg-[#2A2A3C]'
      }`}
    >
      <span
        className="absolute w-[17px] h-[17px] rounded-full bg-white top-[3px] transition-all"
        style={{ left: on ? 20 : 3 }}
      />
    </button>
  );
}

export function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-[#1A1A24] last:border-0 first:pt-0 last:pb-0">
      <span className="text-muted">{label}</span>
      <span className="font-semibold">{children}</span>
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: Array<{ key: T; label: string }>;
}) {
  return (
    <div className="flex gap-1.5 mb-3.5">
      {items.map((it) => (
        <button
          key={it.key}
          onClick={() => onChange(it.key)}
          className={`px-3.5 py-[7px] rounded-lg text-[13px] font-semibold border transition-colors ${
            value === it.key
              ? 'bg-accent border-accent text-white'
              : 'bg-surface border-line text-muted hover:text-ink'
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Всплывающее сообщение.
 *
 * Тост вызывается из любого места простой функцией toast('...'),
 * без прокидывания колбэков через полдерева. Ради одного уведомления
 * тащить стейт-менеджер незачем.
 */
type ToastState = { msg: string; icon?: string; key: number } | null;

let emit: ((s: ToastState) => void) | null = null;
export const toast = (msg: string, icon?: string) => emit?.({ msg, icon, key: Date.now() });

export function Toaster() {
  const [state, setState] = useState<ToastState>(null);

  useEffect(() => {
    emit = setState;
    return () => {
      emit = null;
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    const timer = setTimeout(() => setState(null), 2800);
    return () => clearTimeout(timer);
  }, [state]);

  if (!state) return null;

  return (
    <div
      key={state.key}
      className="fixed right-7 bottom-7 z-50 bg-[#F3F3F8] text-[#0B0B12] px-4 py-3
                 rounded-xl font-semibold text-[13.5px] shadow-2xl flex items-center gap-2.5 animate-fade"
    >
      <span className="text-base">{state.icon ?? '✓'}</span>
      <span>{state.msg}</span>
    </div>
  );
}
